import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Download, Loader2, FileText, File, Video, Music, FileCode, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { attachmentsApi, Attachment, ProjectFile } from '@/lib/api';
import { Button } from './Button';
import { Avatar } from './Avatar';
import { formatBytes, formatDate, cn } from '@/lib/utils';
import { Marked } from 'marked';
import { markedEmoji } from 'marked-emoji';
import DOMPurify from 'dompurify';

// Common GitHub-style emoji shortcodes
const emojiMap: Record<string, string> = {
  // Status/checkmarks
  'white_check_mark': '✅', 'heavy_check_mark': '✔️', 'ballot_box_with_check': '☑️',
  'x': '❌', 'negative_squared_cross_mark': '❎', 'heavy_multiplication_x': '✖️',
  'ok': '🆗', 'warning': '⚠️', 'no_entry': '⛔', 'stop_sign': '🛑',
  // Thumbs
  'thumbsup': '👍', '+1': '👍', 'thumbsdown': '👎', '-1': '👎',
  // Faces
  'smile': '😄', 'grinning': '😀', 'smiley': '😃', 'grin': '😁', 'laughing': '😆',
  'sweat_smile': '😅', 'rofl': '🤣', 'joy': '😂', 'wink': '😉', 'blush': '😊',
  'innocent': '😇', 'thinking': '🤔', 'neutral_face': '😐', 'expressionless': '😑',
  'unamused': '😒', 'roll_eyes': '🙄', 'grimacing': '😬', 'sob': '😭', 'cry': '😢',
  'angry': '😠', 'rage': '😡', 'exploding_head': '🤯', 'flushed': '😳',
  'sunglasses': '😎', 'nerd_face': '🤓', 'monocle_face': '🧐',
  // Hands
  'wave': '👋', 'raised_hands': '🙌', 'clap': '👏', 'handshake': '🤝',
  'pray': '🙏', 'muscle': '💪', 'point_up': '☝️', 'point_down': '👇',
  'point_left': '👈', 'point_right': '👉', 'ok_hand': '👌', 'v': '✌️',
  // Objects
  'star': '⭐', 'star2': '🌟', 'sparkles': '✨', 'zap': '⚡', 'fire': '🔥',
  'boom': '💥', 'heart': '❤️', 'blue_heart': '💙', 'green_heart': '💚',
  'yellow_heart': '💛', 'purple_heart': '💜', 'broken_heart': '💔',
  'bulb': '💡', 'memo': '📝', 'pencil': '✏️', 'pencil2': '✏️',
  'pushpin': '📌', 'paperclip': '📎', 'link': '🔗', 'lock': '🔒', 'unlock': '🔓',
  'key': '🔑', 'hammer': '🔨', 'wrench': '🔧', 'gear': '⚙️', 'tools': '🛠️',
  // Arrows/symbols
  'arrow_up': '⬆️', 'arrow_down': '⬇️', 'arrow_left': '⬅️', 'arrow_right': '➡️',
  'arrow_upper_right': '↗️', 'arrow_lower_right': '↘️', 'arrow_lower_left': '↙️',
  'arrow_upper_left': '↖️', 'arrows_counterclockwise': '🔄', 'rewind': '⏪',
  'fast_forward': '⏩', 'arrow_forward': '▶️', 'arrow_backward': '◀️',
  // Numbers/letters
  'one': '1️⃣', 'two': '2️⃣', 'three': '3️⃣', 'four': '4️⃣', 'five': '5️⃣',
  'six': '6️⃣', 'seven': '7️⃣', 'eight': '8️⃣', 'nine': '9️⃣', 'zero': '0️⃣',
  'keycap_ten': '🔟', 'hash': '#️⃣', 'asterisk': '*️⃣',
  'a': '🅰️', 'b': '🅱️', 'ab': '🆎', 'o2': '🅾️',
  // Info/alerts
  'information_source': 'ℹ️', 'question': '❓', 'grey_question': '❔',
  'exclamation': '❗', 'grey_exclamation': '❕', 'bangbang': '‼️', 'interrobang': '⁉️',
  // Tech
  'computer': '💻', 'desktop_computer': '🖥️', 'keyboard': '⌨️', 'mouse': '🖱️',
  'bug': '🐛', 'robot': '🤖', 'rocket': '🚀', 'satellite': '🛰️',
  // Misc
  'calendar': '📅', 'clock': '🕐', 'hourglass': '⏳', 'alarm_clock': '⏰',
  'bell': '🔔', 'no_bell': '🔕', 'mega': '📣', 'loudspeaker': '📢',
  'speech_balloon': '💬', 'thought_balloon': '💭', 'email': '📧', 'envelope': '✉️',
  'package': '📦', 'gift': '🎁', 'tada': '🎉', 'confetti_ball': '🎊',
  'trophy': '🏆', 'medal_sports': '🏅', 'first_place_medal': '🥇',
  'second_place_medal': '🥈', 'third_place_medal': '🥉', 'crown': '👑',
  'chart_with_upwards_trend': '📈', 'chart_with_downwards_trend': '📉',
  'clipboard': '📋', 'file_folder': '📁', 'open_file_folder': '📂',
  'wastebasket': '🗑️', 'bookmark': '🔖', 'label': '🏷️', 'checkered_flag': '🏁',
};

// Custom bracket syntax mapping [SYMBOL] -> emoji
const bracketEmojiMap: Record<string, string> = {
  '!': '⚠️',
  'OK': '✅',
  'DONE': '✅',
  'YES': '✅',
  'NO': '❌',
  'X': '❌',
  'FAIL': '❌',
  'TODO': '📋',
  'WIP': '🚧',
  'WARNING': '⚠️',
  'WARN': '⚠️',
  'DANGER': '🛑',
  'INFO': 'ℹ️',
  'NOTE': '📝',
  'TIP': '💡',
  'IMPORTANT': '❗',
  'QUESTION': '❓',
  'BUG': '🐛',
  'FIX': '🔧',
  'NEW': '✨',
  'STAR': '⭐',
  'FIRE': '🔥',
  'ROCKET': '🚀',
};

// Replace bracket syntax [SYMBOL] with emoji
function replaceBracketEmojis(text: string): string {
  return text.replace(/\[([A-Z!]+)\]/g, (match, symbol) => {
    return bracketEmojiMap[symbol] || match;
  });
}

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: Attachment | ProjectFile | null;
  attachments?: (Attachment | ProjectFile)[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
}

// Get file type category from MIME type
function getFileCategory(mimeType: string, fileName?: string): 'image' | 'video' | 'audio' | 'pdf' | 'html' | 'markdown' | 'text' | 'office' | 'other' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  // Check for HTML before generic text
  if (mimeType === 'text/html' || fileName?.toLowerCase().match(/\.html?$/)) return 'html';
  // Check for markdown before generic text (some servers return text/plain for .md files)
  if (mimeType === 'text/markdown' || mimeType === 'text/x-markdown' || fileName?.toLowerCase().endsWith('.md')) return 'markdown';
  if (mimeType.startsWith('text/') || mimeType === 'application/json') return 'text';
  // Office documents
  if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-powerpoint' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    fileName?.match(/\.(docx?|xlsx?|pptx?)$/i)
  ) return 'office';
  return 'other';
}

// Get icon for file type
function getFileIcon(mimeType: string, fileName?: string) {
  const category = getFileCategory(mimeType, fileName);
  switch (category) {
    case 'video': return Video;
    case 'audio': return Music;
    case 'pdf': return FileText;
    case 'markdown': return FileText;
    case 'text': return FileCode;
    default: return File;
  }
}

export function FilePreviewModal({ isOpen, onClose, attachment, attachments, currentIndex, onNavigate }: FilePreviewModalProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageZoomed, setImageZoomed] = useState(false);

  const hasMultiple = attachments && attachments.length > 1;
  const canGoPrev = hasMultiple && currentIndex !== undefined && currentIndex > 0;
  const canGoNext = hasMultiple && currentIndex !== undefined && currentIndex < attachments!.length - 1;

  const handlePrev = useCallback(() => {
    if (onNavigate && currentIndex !== undefined && currentIndex > 0) {
      onNavigate(currentIndex - 1);
      setImageZoomed(false);
    }
  }, [onNavigate, currentIndex]);

  const handleNext = useCallback(() => {
    if (onNavigate && currentIndex !== undefined && attachments && currentIndex < attachments.length - 1) {
      onNavigate(currentIndex + 1);
      setImageZoomed(false);
    }
  }, [onNavigate, currentIndex, attachments]);

  // Fetch presigned URL when modal opens
  useEffect(() => {
    if (!isOpen || !attachment) {
      setPreviewUrl(null);
      setError(null);
      setImageZoomed(false);
      return;
    }

    const fetchUrl = async () => {
      setLoading(true);
      setError(null);
      try {
        const { downloadUrl } = await attachmentsApi.getDownloadUrl(attachment.id);
        setPreviewUrl(downloadUrl);
      } catch (err: any) {
        const status = err?.response?.status ?? err?.status;
        setError(status === 404 ? 'This file is no longer available.' : 'Failed to load preview');
        console.error('Failed to fetch preview URL:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUrl();
  }, [isOpen, attachment]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, handlePrev, handleNext]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!attachment || isDownloading) return;
    setIsDownloading(true);
    try {
      const downloadUrl = attachmentsApi.getServerDownloadUrl(attachment.id);
      const token = JSON.parse(localStorage.getItem('solid-hub-auth') || '{}')?.state?.accessToken;
      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [attachment, isDownloading]);

  if (!isOpen || !attachment) return null;

  const category = getFileCategory(attachment.mimeType, attachment.fileName);
  const FileIcon = getFileIcon(attachment.mimeType, attachment.fileName);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-secondary/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative flex w-full h-full animate-scale-in">
        {/* Mobile Close Button - only visible on mobile */}
        <button
          onClick={onClose}
          className="md:hidden absolute top-4 right-4 z-20 p-3 bg-secondary/80 hover:bg-secondary rounded-full transition-colors"
          title="Close"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Preview Area - clicking here closes the modal */}
        <div
          className={cn(
            "flex-1 flex p-4 md:p-8 cursor-pointer",
            category === 'image' || category === 'video' || category === 'audio'
              ? "items-center justify-center overflow-hidden"
              : "items-start justify-center overflow-auto"
          )}
          onClick={onClose}
        >
          {loading ? (
            <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <Loader2 className="w-12 h-12 text-white animate-spin" />
              <p className="text-white/70">Loading preview...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-center" onClick={(e) => e.stopPropagation()}>
              <AlertCircle className="w-12 h-12 text-error" />
              <p className="text-white">{error}</p>
              <Button variant="outline" onClick={handleDownload} disabled={isDownloading}>
                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {isDownloading ? 'Downloading...' : 'Download Instead'}
              </Button>
            </div>
          ) : previewUrl ? (
            <div onClick={(e) => e.stopPropagation()} className={cn(
              category === 'image' ? 'w-full h-full flex items-center justify-center' : '',
              category === 'html' || category === 'markdown' || category === 'text' || category === 'office' || category === 'pdf' ? 'w-full max-w-4xl' : '',
            )}>
              <PreviewContent
                category={category}
                url={previewUrl}
                fileName={attachment.fileName}
                mimeType={attachment.mimeType}
                zoomed={imageZoomed}
                onToggleZoom={() => setImageZoomed(!imageZoomed)}
              />
            </div>
          ) : null}
        </div>

        {/* Prev/Next overlay arrows (shown when multiple attachments) */}
        {hasMultiple && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}
              disabled={!canGoPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-secondary/60 hover:bg-secondary/80 rounded-full transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
              title="Previous (←)"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              disabled={!canGoNext}
              className="absolute right-4 md:right-80 top-1/2 -translate-y-1/2 z-20 p-3 bg-secondary/60 hover:bg-secondary/80 rounded-full transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
              title="Next (→)"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}

        {/* Info Panel - hidden on mobile */}
        <div className="hidden md:flex w-72 bg-secondary flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Close button header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            {/* Navigation arrows (only shown when multiple attachments) */}
            {hasMultiple ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Previous attachment (←)"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <span className="text-white/50 text-xs px-1">
                  {(currentIndex ?? 0) + 1} / {attachments!.length}
                </span>
                <button
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Next attachment (→)"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </div>
            ) : (
              <div />
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* File Info */}
          <div className="flex-1 p-6 space-y-6 overflow-auto">
            {/* File Icon & Name */}
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <FileIcon className="w-6 h-6 text-white/70" />
              </div>
              <div className="min-w-0">
                <h3 className="font-medium text-white break-words">
                  {attachment.fileName}
                </h3>
                <p className="text-sm text-white/60">
                  {formatBytes(attachment.fileSize)}
                </p>
              </div>
            </div>

            {/* Metadata */}
            <div className="space-y-4">
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wide mb-1">Type</p>
                <p className="text-sm text-white">{attachment.mimeType}</p>
              </div>

              {attachment.uploadedBy && (
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-wide mb-2">Uploaded by</p>
                  <div className="flex items-center gap-2">
                    <Avatar 
                      src={attachment.uploadedBy.avatarUrl} 
                      name={attachment.uploadedBy.name} 
                      size="sm" 
                    />
                    <span className="text-sm text-white">{attachment.uploadedBy.name}</span>
                  </div>
                </div>
              )}

              {attachment.createdAt && (
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-wide mb-1">Uploaded on</p>
                  <p className="text-sm text-white">{formatDate(attachment.createdAt)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Download Button */}
          <div className="p-6 border-t border-white/10">
            <Button
              onClick={handleDownload}
              className="w-full bg-white text-secondary hover:bg-white/90"
              disabled={!previewUrl || isDownloading}
            >
              {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Image preview with loading spinner
function ImagePreview({ url, fileName, zoomed, onToggleZoom }: { url: string; fileName: string; zoomed: boolean; onToggleZoom: () => void }) {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
  }, [url]);

  return (
    <div className="relative flex items-center justify-center w-full h-full">
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-white animate-spin" />
        </div>
      )}
      <img
        src={url}
        alt={fileName}
        onClick={onToggleZoom}
        onLoad={() => setImageLoaded(true)}
        className={cn(
          'rounded-lg shadow-lg cursor-zoom-in transition-all duration-200',
          zoomed ? 'max-w-none max-h-none cursor-zoom-out scale-150' : 'max-w-full max-h-full object-contain',
          !imageLoaded && 'invisible'
        )}
      />
    </div>
  );
}

// Preview content component for different file types
function PreviewContent({
  category,
  url,
  fileName,
  mimeType,
  zoomed,
  onToggleZoom,
}: {
  category: 'image' | 'video' | 'audio' | 'pdf' | 'html' | 'markdown' | 'text' | 'office' | 'other';
  url: string;
  fileName: string;
  mimeType: string;
  zoomed: boolean;
  onToggleZoom: () => void;
}) {
  switch (category) {
    case 'image':
      return (
        <ImagePreview
          url={url}
          fileName={fileName}
          zoomed={zoomed}
          onToggleZoom={onToggleZoom}
        />
      );

    case 'video':
      return (
        <video
          src={url}
          controls
          autoPlay={false}
          className="max-w-full max-h-full rounded-lg shadow-lg"
        >
          Your browser does not support video playback.
        </video>
      );

    case 'audio':
      return (
        <div className="flex flex-col items-center gap-6 p-8 bg-white/10 rounded-2xl">
          <Music className="w-24 h-24 text-white/50" />
          <p className="text-white font-medium">{fileName}</p>
          <audio src={url} controls className="w-80">
            Your browser does not support audio playback.
          </audio>
        </div>
      );

    case 'pdf':
      return (
        <iframe
          src={url}
          title={fileName}
          className="w-full bg-white rounded-lg shadow-lg"
          style={{ height: '85vh' }}
        />
      );

    case 'html':
      return (
        <iframe
          src={url}
          title={fileName}
          className="w-full bg-white rounded-lg shadow-lg"
          style={{ height: '85vh' }}
          sandbox="allow-same-origin"
        />
      );

    case 'markdown':
      return <MarkdownPreview url={url} fileName={fileName} />;

    case 'text':
      return <TextPreview url={url} fileName={fileName} />;

    case 'office':
      return <OfficePreview url={url} fileName={fileName} />;

    default:
      return (
        <div className="flex flex-col items-center gap-4 p-8 bg-white/10 rounded-2xl text-center">
          <File className="w-24 h-24 text-white/50" />
          <div>
            <p className="text-white font-medium">{fileName}</p>
            <p className="text-white/60 text-sm mt-1">
              Preview not available for this file type
            </p>
          </div>
        </div>
      );
  }
}

// Office document preview component (via Google Docs Viewer)
function OfficePreview({ url, fileName }: { url: string; fileName: string }) {
  const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
  return (
    <iframe
      src={viewerUrl}
      className="w-full rounded-lg border-0"
      style={{ height: '85vh' }}
      title={fileName}
      sandbox="allow-scripts allow-same-origin allow-popups"
    />
  );
}

// Text file preview component
function TextPreview({ url, fileName }: { url: string; fileName: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(url);
        const text = await response.text();
        setContent(text.slice(0, 50000)); // Limit to 50KB of text
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [url]);

  if (loading) {
    return <Loader2 className="w-8 h-8 text-white animate-spin" />;
  }

  if (error || !content) {
    return (
      <div className="flex flex-col items-center gap-4 p-8 bg-white/10 rounded-2xl">
        <FileCode className="w-16 h-16 text-white/50" />
        <p className="text-white">Could not load text preview</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl max-h-full overflow-auto bg-grey-900 rounded-lg shadow-lg">
      <div className="sticky top-0 bg-grey-800 px-4 py-2 border-b border-grey-700">
        <p className="text-white/70 text-sm">{fileName}</p>
      </div>
      <pre className="p-4 text-sm text-grey-100 font-mono whitespace-pre-wrap break-words">
        {content}
      </pre>
    </div>
  );
}

// Markdown file preview component
function MarkdownPreview({ url, fileName }: { url: string; fileName: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(url);
        const text = await response.text();
        setContent(text.slice(0, 100000)); // Limit to 100KB for markdown
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [url]);

  // Scroll to top when content loads
  useEffect(() => {
    if (content && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [content]);

  // Parse markdown and sanitize HTML
  const renderedHtml = useMemo(() => {
    if (!content) return '';

    // Create a new marked instance with emoji support
    const markedWithEmoji = new Marked();
    markedWithEmoji.use(markedEmoji({ emojis: emojiMap }));

    // First replace bracket syntax [OK] -> ✅, then parse markdown
    const contentWithEmojis = replaceBracketEmojis(content);
    const html = markedWithEmoji.parse(contentWithEmojis, { gfm: true, breaks: true }) as string;

    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target', 'rel'],
    });
  }, [content]);

  if (loading) {
    return <Loader2 className="w-8 h-8 text-white animate-spin" />;
  }

  if (error || !content) {
    return (
      <div className="flex flex-col items-center gap-4 p-8 bg-white/10 rounded-2xl">
        <FileText className="w-16 h-16 text-white/50" />
        <p className="text-white">Could not load markdown preview</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full bg-white rounded-lg shadow-lg">
      <div className="sticky top-0 bg-grey-100 px-4 py-2 border-b border-grey-200 z-10">
        <p className="text-grey-600 text-sm">{fileName}</p>
      </div>
      <div
        className={cn(
          'p-6 prose prose-sm max-w-none',
          'prose-headings:font-serif prose-headings:text-secondary',
          'prose-p:text-secondary prose-p:my-2 prose-p:leading-relaxed',
          'prose-ul:my-2 prose-ol:my-2',
          'prose-li:my-0.5',
          'prose-blockquote:border-l-primary prose-blockquote:text-grey-600 prose-blockquote:not-italic',
          'prose-code:bg-grey-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono',
          'prose-pre:bg-grey-900 prose-pre:text-grey-100 prose-pre:rounded-lg prose-pre:p-4',
          '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
          'prose-a:text-primary prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-primary-600',
          'prose-strong:font-semibold prose-strong:text-secondary',
          'prose-em:italic',
          'prose-img:rounded-lg prose-img:shadow-md'
        )}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
      />
    </div>
  );
}

